import { useAtomValue } from "jotai";
import { isValid, parseISO } from "date-fns";
import { Fragment, useRef } from "react";

import type { ConversationSummary } from "@go-novus/admin-api";
import { ConversationRow, ScrollArea } from "@go-novus/ui";

import {
  conversationListAtom,
  readConversationsFailureMessage,
} from "#/features/conversations";
import * as m from "#/paraglide/messages";
import { formatWorkspaceClock, formatWorkspaceDate } from "#/shared/lib/date";
import { useFailureNotice } from "#/shared/ui/failure-notice";
import { InfiniteScroll } from "#/shared/ui/infinite-scroll";

import { InboxNotice } from "./inbox-notice";

/** 占位行数：撑满一屏之内的高度，多了纯属让人等着看动画。 */
const SKELETON_ROWS = 6;

// 列表独立滚动；游标加载沿用滚动哨兵和键盘入口。
const SCROLL = "min-h-0 flex-1";

const FAILURE_ICON = "info" as const;

/** 解析不出来的时间不分组：契约校验挡在前面，真漏过来也只是少一个日期分隔。 */
function workspaceDay(
  isoDateTime: string,
  timeZone: string,
): string | undefined {
  const parsed = parseISO(isoDateTime);
  return isValid(parsed)
    ? formatWorkspaceDate(parsed.getTime(), timeZone)
    : undefined;
}

function conversationCountLabel(count: number): string {
  return count === 1
    ? m.inbox_conversation_message_count_one()
    : m.inbox_conversation_message_count({ count });
}

/** 一会话一行，标签使用后端访客编号，按原始会话 id 选择。 */
function ConversationListRow({
  conversation,
  onSelect,
  selectedConversationId,
  timeZone,
}: {
  conversation: ConversationSummary;
  onSelect: (conversationId: string) => void;
  selectedConversationId: string | undefined;
  timeZone: string | undefined;
}) {
  return (
    <ConversationRow
      onClick={() => {
        onSelect(conversation.id);
      }}
      preview={
        conversation.lastMessagePreview ??
        conversationCountLabel(conversation.messageCount)
      }
      selected={conversation.id === selectedConversationId}
      source={
        conversation.handoffRequestSent
          ? m.inbox_conversation_request_sent()
          : conversation.handoff
            ? m.inbox_conversation_handoff()
            : undefined
      }
      grounded={false}
      timestamp={
        timeZone === undefined
          ? undefined
          : formatWorkspaceClock(conversation.lastMessageAt, timeZone)
      }
      visitor={conversation.visitorLabel}
    />
  );
}

interface ConversationListBodyProps {
  conversations: readonly ConversationSummary[];
  failureMessage: string | null;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isPending: boolean;
  onLoadMore: () => void;
  onSelect: (conversationId: string) => void;
  selectedConversationId: string | undefined;
  timeZone: string | undefined;
}

function ConversationListBody({
  conversations,
  failureMessage,
  hasNextPage,
  isFetchingNextPage,
  isPending,
  onLoadMore,
  onSelect,
  selectedConversationId,
  timeZone,
}: ConversationListBodyProps) {
  // 列表在这个容器里滚，不是跟着页面滚，所以哨兵要按它算交叉。
  const scrollRef = useRef<HTMLDivElement>(null);

  if (failureMessage !== null) {
    return <InboxNotice heading={failureMessage} iconName={FAILURE_ICON} />;
  }

  if (isPending) {
    return (
      <div className={SCROLL}>
        <ScrollArea height="100%">
          <span className="sr-only" role="status">
            {m.inbox_list_loading()}
          </span>
          {Array.from({ length: SKELETON_ROWS }, (_, row) => (
            <ConversationRow key={row} loading />
          ))}
        </ScrollArea>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <InboxNotice
        body={m.inbox_list_empty_body()}
        heading={m.inbox_list_empty_heading()}
      />
    );
  }

  return (
    <div className={SCROLL}>
      <ScrollArea height="100%" viewportRef={scrollRef}>
        <div>
          {conversations.map((conversation, index) => {
            const day =
              timeZone === undefined
                ? undefined
                : workspaceDay(conversation.lastMessageAt, timeZone);
            const previous = conversations[index - 1];
            const previousDay =
              previous === undefined || timeZone === undefined
                ? undefined
                : workspaceDay(previous.lastMessageAt, timeZone);
            return (
              <Fragment key={conversation.id}>
                {day !== undefined && day !== previousDay && (
                  <h3 className="border-b border-[var(--border-surface-default)] bg-[var(--bg-surface-raised)] px-4 py-2 text-micro font-semibold text-[var(--content-fg-subtle)]">
                    {day}
                  </h3>
                )}
                <ConversationListRow
                  conversation={conversation}
                  onSelect={onSelect}
                  selectedConversationId={selectedConversationId}
                  timeZone={timeZone}
                />
              </Fragment>
            );
          })}
        </div>
        <InfiniteScroll
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          label={m.inbox_list_load_more()}
          loadingLabel={m.inbox_list_loading_more()}
          loadingContent={
            <div aria-hidden="true">
              {Array.from({ length: 3 }, (_, row) => (
                <ConversationRow key={row} loading />
              ))}
            </div>
          }
          onLoadMore={onLoadMore}
          rootRef={scrollRef}
        />
      </ScrollArea>
    </div>
  );
}

export interface ConversationListProps {
  onSelect: (conversationId: string) => void;
  selectedConversationId: string | undefined;
  timeZone: string | undefined;
}

/** 真实买家会话列表：一会话一行，按最近活跃倒序，游标翻页。 */
export function ConversationList({
  onSelect,
  selectedConversationId,
  timeZone,
}: ConversationListProps) {
  const list = useAtomValue(conversationListAtom);
  const conversations = list.data?.pages.flatMap((page) => page.items) ?? [];
  const blockingFailure = useFailureNotice({
    failedAt: list.errorUpdatedAt,
    hasContent: list.data !== undefined,
    message: list.isError
      ? (readConversationsFailureMessage(list.error) ??
        m.inbox_conversations_failure_unexpected())
      : null,
  });

  return (
    <div className="flex min-h-0 flex-col">
      <div className="flex min-h-17 flex-none items-center border-b border-[var(--border-surface-default)] px-4 py-3 text-headline font-bold text-[var(--content-fg-strong)]">
        {m.inbox_list_heading()}
      </div>
      <ConversationListBody
        conversations={conversations}
        failureMessage={blockingFailure}
        hasNextPage={list.hasNextPage}
        isFetchingNextPage={list.isFetchingNextPage}
        isPending={list.isPending}
        onLoadMore={() => {
          void list.fetchNextPage();
        }}
        onSelect={onSelect}
        selectedConversationId={selectedConversationId}
        timeZone={timeZone}
      />
    </div>
  );
}
